import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import Icon from '../../../components/AppIcon';
import QuestionCard from './QuestionCard';
import Button from '../../../components/ui/Button';
import LocationPickerMap from '../../../components/LocationPickerMap';
import { extractCityDistrict } from '../../../utils/addressParsing';
import { calculateDistance } from '../../../services/locationService';
import { normalizeCity } from '../../../utils/cityNormalization';
import { buildPreciseLocationFromSelection } from '../../../utils/preciseLocationBuilder';
import { useUserLocation } from '../../../contexts/UserLocationContext';
import {
  getCurrentPosition,
  isGeolocationSupported,
  mapGeolocationErrorToStatus,
} from '../../../utils/location';

const PreciseLocationStep = ({ formData, onUpdate, isDirectMode, preselectedPhotographer }) => {
  const navigate = useNavigate();
  const { userLocation: contextLocation } = useUserLocation();
  const [mapVisible, setMapVisible] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [error, setError] = useState(null);
  const [serviceRadiusError, setServiceRadiusError] = useState(null);
  const [showOutOfRangeModal, setShowOutOfRangeModal] = useState(false);
  const [calculatedDistance, setCalculatedDistance] = useState(null);
  const didInitRef = useRef(false);
  const didRunInitialRangeCheckRef = useRef(false);

  // Hide bottom nav when map modal is open
  useEffect(() => {
    if (mapVisible) {
      document.body.classList.add('map-modal-open');
    } else {
      document.body.classList.remove('map-modal-open');
    }
    return () => document.body.classList.remove('map-modal-open');
  }, [mapVisible]);

  // SAFE MODE: عند تحميل الخطوة، إذا كان هناك موقع مُمرر من الهيدر تحقق من نطاق خدمة المصور
  useEffect(() => {
    if (!isDirectMode || !preselectedPhotographer || didRunInitialRangeCheckRef.current) return;
    const pl = formData?.preciseLocation;
    const lat = pl?.lat != null ? (typeof pl.lat === 'number' ? pl.lat : parseFloat(pl.lat)) : null;
    const lng = pl?.lng != null ? (typeof pl.lng === 'number' ? pl.lng : parseFloat(pl.lng)) : null;
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng) || !validateCoords({ lat, lng })) return;
    didRunInitialRangeCheckRef.current = true;

    const { serviceCenterLat, serviceCenterLng, serviceRadiusKm } = getPhotographerServiceData();

    // إذا لم تتوفر بيانات نطاق المصور، اعتبر الموقع داخل النطاق وأنهِ الحالة المعلّقة
    if (!serviceCenterLat || !serviceCenterLng) {
      onUpdate({ isOutsideServiceRange: false, distanceFromPhotographerKm: null });
      return;
    }

    const distanceKm = calculateDistance(
      parseFloat(lat),
      parseFloat(lng),
      parseFloat(serviceCenterLat),
      parseFloat(serviceCenterLng)
    );
    setCalculatedDistance(distanceKm);

    if (distanceKm > serviceRadiusKm) {
      const errorMsg = `موقع التصوير خارج نطاق خدمة المصور (${serviceRadiusKm} كم).`;
      setServiceRadiusError(errorMsg);
      setShowOutOfRangeModal(true);
      onUpdate({
        isOutsideServiceRange: true,
        distanceFromPhotographerKm: distanceKm,
        serviceRadiusKm,
        ...(formData?.exceptionRequested !== true ? { exceptionRequested: false } : {})
      });
    } else {
      // داخل النطاق — يجب تعيين false صريحاً لإنهاء الحالة المعلّقة وتفعيل زر التالي
      onUpdate({
        isOutsideServiceRange: false,
        distanceFromPhotographerKm: distanceKm,
        serviceRadiusKm,
      });
    }
  }, [isDirectMode, preselectedPhotographer, formData?.preciseLocation?.lat, formData?.preciseLocation?.lng, formData?.exceptionRequested]);

  // SAFE MODE: Region coordinates for initial map center
  const regionCoordinates = {
    eastern: { lat: 26.4207, lng: 50.0888, label: 'الشرقية' }, // Dammam
    central: { lat: 24.7136, lng: 46.6753, label: 'الوسطى' }, // Riyadh
    western: { lat: 21.4858, lng: 39.1925, label: 'الغربية' }, // Jeddah
    northern: { lat: 28.3998, lng: 36.5700, label: 'الشمالية' }, // Tabuk
    southern: { lat: 18.2164, lng: 42.5042, label: 'الجنوبية' } // Abha
  };

  // City coordinates for Saudi cities (approximate centers) - kept for backward compatibility
  const cityCoordinates = {
    riyadh: { lat: 24.7136, lng: 46.6753, label: 'الرياض' },
    jeddah: { lat: 21.4858, lng: 39.1925, label: 'جدة' },
    dammam: { lat: 26.4207, lng: 50.0888, label: 'الدمام' },
    mecca: { lat: 21.3891, lng: 39.8579, label: 'مكة المكرمة' },
    medina: { lat: 24.5247, lng: 39.5692, label: 'المدينة المنورة' },
    khobar: { lat: 26.2172, lng: 50.1971, label: 'الخبر' },
    taif: { lat: 21.2703, lng: 40.4158, label: 'الطائف' },
    tabuk: { lat: 28.3998, lng: 36.5700, label: 'تبوك' },
    abha: { lat: 18.2164, lng: 42.5042, label: 'أبها' },
    buraidah: { lat: 26.3260, lng: 43.9750, label: 'بريدة' }
  };

  // Initialize local state from formData only once
  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
    }
  }, []);

  // Validate coordinates
  const validateCoords = (coords) => {
    if (!coords) return false;
    const { lat, lng } = coords;
    if (typeof lat !== 'number' || typeof lng !== 'number') return false;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    // Prevent zero/default coords
    if (lat === 0 && lng === 0) return false;
    if (Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001) return false;
    // Basic bounds
    if (lat < -90 || lat > 90) return false;
    if (lng < -180 || lng > 180) return false;
    return true;
  };

  // Get photographer service data
  const getPhotographerServiceData = () => {
    const serviceCenterLat = preselectedPhotographer?.serviceCenterLat || 
                            preselectedPhotographer?.service_center_lat ||
                            formData?.directPhotographerProfile?.serviceCenterLat;
    const serviceCenterLng = preselectedPhotographer?.serviceCenterLng || 
                            preselectedPhotographer?.service_center_lng ||
                            formData?.directPhotographerProfile?.serviceCenterLng;
    const serviceRadiusKm = preselectedPhotographer?.serviceRadiusKm || 
                           preselectedPhotographer?.service_radius_km ||
                           formData?.directPhotographerProfile?.serviceRadiusKm ||
                           20;
    const photographerCity = preselectedPhotographer?.city || 
                            formData?.directPhotographerProfile?.city ||
                            formData?.cityLabel || 
                            formData?.city;
    const photographerName = preselectedPhotographer?.user?.full_name || 
                            preselectedPhotographer?.name ||
                            formData?.directPhotographerProfile?.name ||
                            'المصور';
    
    return { serviceCenterLat, serviceCenterLng, serviceRadiusKm, photographerCity, photographerName };
  };

  // Validate service radius for direct booking
  // SAFE MODE: Returns validation result and distance info for modal handling
  const validateServiceRadius = (latitude, longitude) => {
    if (!isDirectMode || !preselectedPhotographer) {
      return { valid: true, error: null, distance: null };
    }

    const { serviceCenterLat, serviceCenterLng, serviceRadiusKm } = getPhotographerServiceData();

    // SAFE MODE: If photographer has no service center coords, allow booking with warning
    if (!serviceCenterLat || !serviceCenterLng) {
      console.log('⚠️ Photographer has no service center coordinates, allowing booking');
      return { valid: true, error: null, distance: null, noCoords: true };
    }

    const distanceKm = calculateDistance(
      parseFloat(latitude),
      parseFloat(longitude),
      parseFloat(serviceCenterLat),
      parseFloat(serviceCenterLng)
    );

    // Store distance for booking data
    setCalculatedDistance(distanceKm);

    if (distanceKm > serviceRadiusKm) {
      return {
        valid: false,
        error: `موقع التصوير خارج نطاق خدمة المصور (${serviceRadiusKm} كم).`,
        distance: distanceKm,
        serviceRadius: serviceRadiusKm,
        isOutsideRange: true
      };
    }

    return { valid: true, error: null, distance: distanceKm, isOutsideRange: false };
  };

  // SAFE MODE: Handle exception request - saves location but marks as outside range
  const handleRequestException = () => {
    const { serviceRadiusKm } = getPhotographerServiceData();
    
    // Update booking data with exception flag
    onUpdate({
      isOutsideServiceRange: true,
      exceptionRequested: true,
      distanceFromPhotographerKm: calculatedDistance,
      serviceRadiusKm: serviceRadiusKm
    });
    
    setShowOutOfRangeModal(false);
    setServiceRadiusError(null);
    setError(null);
  };

  // SAFE MODE: Navigate to find closer photographers
  const handleFindCloserPhotographers = () => {
    setShowOutOfRangeModal(false);
    // Navigate to photographer search with location data
    navigate('/browse-photographers', { 
      state: { 
        fromLocation: formData?.preciseLocation,
        searchNearby: true 
      } 
    });
  };

  // Check if context is secure (Capacitor/native and web)
  const checkSecureContext = () => {
    const isNative = Capacitor.isNativePlatform();
    if (isNative) return true; // capacitor:// or app URL is treated as secure for geolocation
    const hostname = window.location.hostname;
    const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
    return window.isSecureContext || 
           window.location.protocol === 'https:' || 
           hostname === 'localhost' || 
           hostname === '127.0.0.1' ||
           isLocalNetwork;
  };

  const requestGeolocationPermission = async () => {
    if (!isGeolocationSupported()) {
      setError('المتصفح لا يدعم تحديد الموقع الجغرافي. يمكنك استخدام الخريطة لتحديد موقعك.');
      return false;
    }
    const isSecure = checkSecureContext();
    if (!isSecure) {
      setError('لتفعيل تحديد الموقع، افتح الموقع عبر https أو عبر localhost. يمكنك استخدام الخريطة لتحديد موقعك.');
      return false;
    }
    try {
      await getCurrentPosition({ enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
      return true;
    } catch (error) {
      const status = mapGeolocationErrorToStatus(error);
      if (status === 'denied') {
        setError('تم رفض إذن الموقع. يمكنك إعادة الطلب من الزر أدناه أو تحديد الموقع يدوياً على الخريطة.');
      } else if (status === 'timeout') {
        setError('انتهت مهلة تحديد الموقع. يمكنك تحديد الموقع يدوياً على الخريطة.');
      } else {
        setError('فشل تحديد موقعك الحالي. يمكنك تحديد الموقع يدوياً على الخريطة.');
      }
      return false;
    }
  };

  // Reverse geocode using Google Maps Geocoding API
  const reverseGeocode = async (lat, lng) => {
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!googleMapsApiKey) {
      return null;
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&language=ar&key=${googleMapsApiKey}`
      );
      
      const data = await response.json();
      
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        return data.results[0];
      } else {
        return null;
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // SAFE MODE: Handle location selection from map - SIMPLIFIED for reliability
  // This function is called when user clicks "حفظ الموقع" in the map modal
  const handleMapSelect = (selection) => {
    // selection = { lat, lng, address, city, district } from LocationPickerMap
    
    // STEP 1: Extract and validate coordinates immediately
    const rawLat = selection?.lat;
    const rawLng = selection?.lng;
    
    // Ensure we have numbers
    const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat);
    const lng = typeof rawLng === 'number' ? rawLng : parseFloat(rawLng);
    
    // STRICT validation - reject if coordinates are invalid
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
      setError('إحداثيات الموقع غير صالحة. يرجى تحديد موقع على الخريطة أولاً.');
      return;
    }
    
    // Additional validation using validateCoords helper
    if (!validateCoords({ lat, lng })) {
      setError('الرجاء اختيار موقع صحيح على الخريطة');
      return;
    }

    // STEP 2: Clear any existing errors
    setError(null);
    setServiceRadiusError(null);

    // STEP 3: Validate service radius in direct mode (if applicable)
    if (isDirectMode && preselectedPhotographer) {
      const validation = validateServiceRadius(lat, lng);
      if (!validation.valid && validation.isOutsideRange) {
        // SAFE MODE: Show modal instead of blocking
        setServiceRadiusError(validation.error);
        // Still save the location but show modal for user choice
        // Don't return - let the location be saved first
      }
    }

    // STEP 4: Build the complete location object with all available data (shared helper)
    const updateData = buildPreciseLocationFromSelection(
      {
        ...selection,
        lat,
        lng,
      },
      formData
    );

    if (!updateData || !updateData.preciseLocation) {
      setError('إحداثيات الموقع غير صالحة. يرجى تحديد موقع على الخريطة أولاً.');
      return;
    }

    // SAFE MODE: Add distance data for direct booking
    if (isDirectMode && preselectedPhotographer) {
      const validation = validateServiceRadius(
        updateData.preciseLocation.lat,
        updateData.preciseLocation.lng
      );
      if (validation.distance !== null) {
        updateData.distanceFromPhotographerKm = validation.distance;
        updateData.isOutsideServiceRange = validation.isOutsideRange || false;
      }
      
      // Show modal if outside range
      if (validation.isOutsideRange) {
        // Save location first, then show modal
        onUpdate(updateData);
        setMapVisible(false);
        setShowOutOfRangeModal(true);
        return;
      }
    }

    // STEP 7: Call onUpdate to save the location (this updates formDataRef immediately)
    onUpdate(updateData);

    // STEP 8: Close the map modal
    setMapVisible(false);
  };

  const handleUseCurrentLocation = async () => {
    const processPosition = async (lat, lng) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !validateCoords({ lat, lng })) {
        setLoadingAddress(false);
        setError('تعذر تحديد موقعك بدقة، حاول اختيار الموقع من الخريطة');
        return;
      }
      if (isDirectMode && preselectedPhotographer) {
        const validation = validateServiceRadius(lat, lng);
        if (!validation.valid) {
          setLoadingAddress(false);
          setServiceRadiusError(validation.error);
          setError(validation.error);
          return;
        }
      }
      let finalCity = formData?.cityLabel || formData?.city || 'الرياض';
      let finalDistrict = null;
      let finalAddress = `موقعك الحالي، ${finalCity}`;
      try {
        const geocoderResult = await reverseGeocode(lat, lng);
        if (geocoderResult) {
          const extracted = extractCityDistrict(geocoderResult);
          finalCity = extracted.city || finalCity;
          finalDistrict = extracted.district || null;
          finalAddress = finalDistrict ? `الموقع: ${finalCity} — ${finalDistrict}` : `الموقع: ${finalCity}`;
        }
      } catch (e) {}
      const { cityKey, cityLabel: normalizedLabel } = normalizeCity(finalCity, formData?.region);
      const preciseLocation = {
        lat, lng, address_text: finalAddress,
        city: cityKey || finalCity, district: finalDistrict, source: 'gps'
      };
      const updateData = { preciseLocation };
      if (cityKey) {
        updateData.city = cityKey;
        updateData.cityLabel = normalizedLabel || finalCity;
      } else {
        updateData.city = finalCity;
        updateData.cityLabel = finalCity;
      }
      if (finalDistrict) updateData.district = finalDistrict;
      onUpdate(updateData);
      setLoadingAddress(false);
      setServiceRadiusError(null);
    };

    setError(null);
    setLoadingAddress(true);

    if (!isGeolocationSupported()) {
      setLoadingAddress(false);
      setError('المتصفح لا يدعم تحديد الموقع');
      return;
    }
    try {
      const pos = await getCurrentPosition({ enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
      await processPosition(pos?.coords?.latitude, pos?.coords?.longitude);
    } catch {
      setLoadingAddress(false);
      setError('لم يتم السماح بتحديد الموقع، اختر الموقع من الخريطة');
    }
  };

  // Get map center
  const getMapCenter = () => {
    // Priority 0: User location from app context (onboarding fast + accurate pipeline)
    if (contextLocation?.lat != null && contextLocation?.lng != null) {
      return {
        lat: Number(contextLocation.lat),
        lng: Number(contextLocation.lng),
      };
    }

    // Priority 1: Saved location for this booking step
    if (formData?.preciseLocation?.lat && formData?.preciseLocation?.lng) {
      return {
        lat: formData.preciseLocation.lat,
        lng: formData.preciseLocation.lng,
      };
    }
    
    // Priority 2: Selected region center (SAFE MODE: new flow)
    if (formData?.region && regionCoordinates[formData.region]) {
      return { 
        lat: regionCoordinates[formData.region].lat, 
        lng: regionCoordinates[formData.region].lng 
      };
    }
    
    // Priority 3: Selected city center (backward compatibility)
    if (formData?.city) {
      let cityKey = formData.city;
      
      if (!cityCoordinates[cityKey]) {
        const foundCity = Object.entries(cityCoordinates).find(
          ([key, value]) => value.label === cityKey || value.label === formData?.cityLabel
        );
        if (foundCity) {
          cityKey = foundCity[0];
        }
      }
      
      const cityData = cityCoordinates[cityKey];
      if (cityData) {
        return { lat: cityData.lat, lng: cityData.lng };
      }
    }
    
    // Priority 4: Fallback to Riyadh (central region)
    return { lat: regionCoordinates.central.lat, lng: regionCoordinates.central.lng };
  };

  const isLocationSet = formData?.preciseLocation?.lat && 
                        formData?.preciseLocation?.lng && 
                        validateCoords({ lat: formData.preciseLocation.lat, lng: formData.preciseLocation.lng });

  // SAFE MODE: Check if ready to show map (region OR city OR direct mode)
  const canShowMap = isDirectMode || formData?.region || formData?.city;

  return (
    <QuestionCard
      icon="MapPin"
      title="حدد الموقع الدقيق"
      description="حدد موقع جلسة التصوير على الخريطة. سيتم استخراج المدينة والحي تلقائياً."
    >
      {!canShowMap && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-warning">
            <Icon name="AlertCircle" size={20} />
            <span className="text-sm font-medium">يرجى اختيار المنطقة أولاً</span>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* SAFE MODE: Photographer Info Card (Direct Mode) - Replaces City Selection */}
        {isDirectMode && preselectedPhotographer && (
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-5 mb-4">
            <div className="flex items-center gap-4">
              {/* Photographer Avatar */}
              {(preselectedPhotographer.user?.avatar_url || preselectedPhotographer.avatar || formData?.directPhotographerProfile?.avatar) && (
                <img 
                  src={preselectedPhotographer.user?.avatar_url || preselectedPhotographer.avatar || formData?.directPhotographerProfile?.avatar}
                  alt={getPhotographerServiceData().photographerName}
                  className="w-14 h-14 rounded-full object-cover border-2 border-primary/20"
                />
              )}
              {!(preselectedPhotographer.user?.avatar_url || preselectedPhotographer.avatar || formData?.directPhotographerProfile?.avatar) && (
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                  <Icon name="User" size={24} className="text-primary" />
                </div>
              )}
              
              <div className="flex-1 text-right">
                <h3 className="font-bold text-slate-900 text-lg">
                  {getPhotographerServiceData().photographerName}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                  <span className="flex items-center gap-1 text-slate-600">
                    <Icon name="MapPin" size={14} className="text-primary" />
                    {getPhotographerServiceData().photographerCity}
                  </span>
                  <span className="flex items-center gap-1 text-slate-600">
                    <Icon name="Target" size={14} className="text-primary" />
                    نطاق الخدمة: {getPhotographerServiceData().serviceRadiusKm} كم
                  </span>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground mt-3 text-right">
              حدد موقع جلسة التصوير على الخريطة
            </p>
          </div>
        )}

        {/* Map Button */}
        <Button
          variant="default"
          fullWidth
          onClick={() => {
            setError(null);
            setServiceRadiusError(null);
            setMapVisible(true);
            requestGeolocationPermission();
          }}
          disabled={!canShowMap}
          iconName="Map"
          iconPosition="right"
          className="shadow-md hover:shadow-lg transition-shadow"
        >
          {isLocationSet ? 'تعديل الموقع' : 'تحديد على الخريطة'}
        </Button>

        {/* Error Message */}
        {(error || serviceRadiusError) && (
          <div className={`border rounded-lg p-4 ${
            serviceRadiusError 
              ? 'bg-destructive/10 border-destructive/20' 
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-start gap-3">
              <Icon 
                name="AlertCircle" 
                size={20} 
                className={`flex-shrink-0 mt-0.5 ${
                  serviceRadiusError ? 'text-destructive' : 'text-amber-600'
                }`} 
              />
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  serviceRadiusError ? 'text-destructive' : 'text-amber-800'
                }`}>
                  {error || serviceRadiusError}
                </p>
                {error && !serviceRadiusError && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setError(null);
                      requestGeolocationPermission();
                    }}
                    iconName="RefreshCw"
                    iconPosition="right"
                  >
                    إعادة طلب الإذن
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SAFE MODE: Location Confirmed - Show ONE unified location text */}
        {isLocationSet && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={24} className="text-success flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-right">
                <p className="font-semibold text-success mb-2">تم تحديد الموقع</p>
                
                {/* SAFE MODE: Show ONE field only - address_text contains city + district */}
                <p className="text-sm text-slate-700">
                  {formData?.preciseLocation?.address_text || 
                   (formData?.preciseLocation?.city && formData?.preciseLocation?.district 
                     ? `${formData.preciseLocation.city} — ${formData.preciseLocation.district}`
                     : formData?.preciseLocation?.city || 'موقع محدد')}
                </p>
                
                {formData?.preciseLocation?.source === 'gps' && (
                  <p className="text-xs text-slate-500 mt-2">📍 باستخدام GPS</p>
                )}
                {formData?.preciseLocation?.source === 'pin' && (
                  <p className="text-xs text-slate-500 mt-2">📍 من الخريطة</p>
                )}
                
                {/* SAFE MODE: Show distance info in direct mode */}
                {isDirectMode && formData?.distanceFromPhotographerKm && (
                  <p className={`text-xs mt-2 ${formData?.isOutsideServiceRange ? 'text-amber-600' : 'text-slate-500'}`}>
                    📏 المسافة من موقع المصور: {formData.distanceFromPhotographerKm.toFixed(1)} كم
                    {formData?.isOutsideServiceRange && formData?.exceptionRequested && (
                      <span className="text-primary mr-2">✓ تم طلب استثناء</span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* SAFE MODE: Within Range Success Message (Direct Mode) */}
        {isDirectMode && isLocationSet && !formData?.isOutsideServiceRange && formData?.distanceFromPhotographerKm && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-3 mt-2">
            <p className="text-sm text-success font-medium text-center">
              ✓ موقع التصوير داخل نطاق خدمة المصور
            </p>
          </div>
        )}
      </div>
      
      {/* Full-Screen Map Modal */}
      {mapVisible && canShowMap && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col overflow-hidden" dir="rtl">
          {/* Modal Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 border-b border-slate-200 bg-white shadow-sm" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)', paddingBottom: '12px' }}>
            <span className="text-base font-bold text-slate-900">تحديد موقع الجلسة</span>
            <button
              onClick={() => setMapVisible(false)}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              aria-label="إغلاق"
            >
              <Icon name="X" size={22} className="text-slate-600" />
            </button>
          </div>

          {/* Map Content */}
          <div className="flex-1 overflow-y-auto">
            <LocationPickerMap
              key={`map-${formData?.preciseLocation?.lat || 'default'}-${formData?.preciseLocation?.lng || 'default'}`}
              initialCenter={getMapCenter()}
              onLocationSelect={handleMapSelect}
              onCancel={() => setMapVisible(false)}
            />
          </div>
        </div>
      )}

      {/* SAFE MODE: Out of Range Modal */}
      {showOutOfRangeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center" dir="rtl">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-amber-600" />
            </div>
            
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              موقع التصوير خارج نطاق الخدمة
            </h3>
            
            <p className="text-slate-600 mb-4">
              الموقع المحدد يبعد <strong className="text-amber-600">{calculatedDistance?.toFixed(1)} كم</strong> عن موقع المصور.
              <br />
              نطاق خدمة المصور: <strong>{getPhotographerServiceData().serviceRadiusKm} كم</strong>
            </p>
            
            <div className="space-y-3">
              <Button
                variant="default"
                fullWidth
                onClick={handleFindCloserPhotographers}
                iconName="Search"
                iconPosition="right"
                className="bg-primary text-white hover:bg-primary/90"
              >
                اعرض مصورين أقرب لموقعي
              </Button>
              
              <Button
                variant="ghost"
                fullWidth
                onClick={() => {
                  setShowOutOfRangeModal(false);
                  // Clear location to let user try again
                  onUpdate({ 
                    preciseLocation: null, 
                    distanceFromPhotographerKm: null,
                    isOutsideServiceRange: null 
                  });
                }}
                iconName="X"
                iconPosition="right"
                className="text-slate-500"
              >
                اختر موقعاً آخر
              </Button>
            </div>
          </div>
        </div>
      )}
    </QuestionCard>
  );
};

export default PreciseLocationStep;
